import { computed } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { TopicState } from './topic.state';
import { Topic } from '../../models/topic';
import { StateFilter } from '../crud.state';

export const filterItems = (items: Topic[], filter: StateFilter) => {
  return items.filter(item => {
    const titleMatch = item.title
      .toLowerCase()
      .includes(filter.query.toLowerCase());

    const idMatch =
      !filter.ids ||
      filter.ids.length === 0 ||
      filter.ids.includes(item._id) ||
      filter.ids.includes(item._id);
    return titleMatch && idMatch;
  });
};

export function withTopicSelectors() {
  return signalStoreFeature(
    {
      state: type<TopicState>(),
    },
    withComputed(({ items, filter }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() => Object.values(items()) as Topic[]),
      sorted: computed(
        () =>
          Object.values(items()).sort(
            (a, b) => a.sorting - b.sorting
          ) as Topic[]
      ),
      filteredSorted: computed(() => {
        let filtered = Object.values(items());
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered.sort((a, b) =>
          a.sorting < b.sorting ? -1 : 1
        ) as Topic[];
      }),
      /**
       * Reverse index from bookmark-category id to the topics that reference
       * it, built once per topics change instead of re-scanning all topics on
       * every `findByBookmarkCategory` call.
       */
      byBookmarkCategory: computed(() => {
        const map: Record<string, Topic[]> = {};
        for (const topic of Object.values(items()) as Topic[]) {
          for (const categoryId of topic.bookmarkCategories) {
            (map[categoryId] ??= []).push(topic);
          }
        }
        return map;
      }),
    })),
    withMethods(store => ({
      globalSearch(query: string, tags: string[]): Topic[] {
        const itemsArray = Object.values(store.items());
        const tempFilter: StateFilter = { ...store.filter(), query, tags };
        return filterItems(itemsArray, tempFilter);
      },
      hasBookmarkCategory(id: string, categoryId: string): boolean {
        return (
          store.items()[id]?.bookmarkCategories.includes(categoryId) || false
        );
      },
      findByBookmarkCategory(categoryId: string): Topic[] {
        return store.byBookmarkCategory()[categoryId] || [];
      },
    }))
  );
}
